import React, { useId, useState } from 'react';
import { Box, Chip, Popover, Typography } from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Tone palettes follow the existing insight severity colors used by
// MorningBriefPanel / InsightCard (amber + red), plus quiet navy/green
// variants for informational and on-track states. Deliberately muted so
// inline hints never shout over the page content.
const TONES = {
  info: { color: '#000062', bg: '#eef0fb', border: '#b3b8e8' },
  success: { color: '#1e6b33', bg: '#eaf5ec', border: '#a8d4b2' },
  warning: { color: '#b54708', bg: '#fff8e1', border: '#ffd58a' },
  error: { color: '#c62828', bg: '#fff5f5', border: '#ef9a9a' },
};

/**
 * Small inline AI-hint chip with a details popover.
 *
 * Reusable pattern for surfacing model output in context (job duration
 * estimates, route risk badges, ...). Renders a quiet, dismissible chip;
 * clicking it opens a popover with the detail content plus a standard
 * "AI estimate" disclaimer, so every inline hint behaves consistently.
 *
 * Props:
 * - label:     chip text (already translated)
 * - tone:      'info' | 'success' | 'warning' | 'error'
 * - ariaLabel: accessible label for the chip button
 * - title:     popover heading (already translated)
 * - children:  popover body content
 */
const InlineInsightChip = ({ label, tone = 'info', ariaLabel, title, children, sx }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const popoverId = useId();
  const palette = TONES[tone] || TONES.info;
  const open = Boolean(anchorEl);

  return (
    <>
      <Chip
        size="small"
        icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: `${palette.color} !important` }} aria-hidden="true" />}
        label={label}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={ariaLabel || label}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? popoverId : undefined}
        sx={{
          height: 22,
          fontSize: '0.72rem',
          fontWeight: 600,
          color: palette.color,
          bgcolor: palette.bg,
          border: '1px solid',
          borderColor: palette.border,
          '&:hover': { bgcolor: palette.bg, filter: 'brightness(0.97)' },
          ...sx,
        }}
      />
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 320 } } }}
      >
        {title && (
          <Typography variant="subtitle2" component="h4" sx={{ mb: 0.5, color: palette.color }}>
            {title}
          </Typography>
        )}
        <Box sx={{ '& > *': { mb: 0.5 } }}>{children}</Box>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0 }}>
          {t('insights.inline.disclaimer')}
        </Typography>
      </Popover>
    </>
  );
};

export default InlineInsightChip;
